import { Portal, Stack, Theme, Toolbar, Typography, useMediaQuery } from '@mui/material';
import { useCurrentUser } from 'api';
import { isErrored } from 'api/utils';
import NavDrawer from 'components/NavDrawer';
import React, { useContext, useState } from 'react';

import { drawer } from 'config/responsive';

import * as Styled from './Header.style';
import * as Items from './items';
import UserProfileMenu from './UserProfileMenu';

function getInitialShellMode(): 'default' | 'headless' {
  if (typeof window === 'undefined') {
    return 'default';
  }

  const params = new URLSearchParams(window.location.search);
  return params.get('headless') === 'true' ? 'headless' : 'default';
}

const HeaderMixinContext = React.createContext<{
  ref: HTMLDivElement | null;
}>({
  ref: null,
});

/**
 * @summary Represents header component with contents logo and logout
 * @namespace Header
 * @return {ReactJSXElement} jsx component
 */
export default function Header({ children }: { children: React.ReactNode }) {
  const userProfile = useCurrentUser();
  const [navOpen, setNavOpen] = React.useState(false);
  const [shellMode, setShellMode] = React.useState<'default' | 'headless'>(getInitialShellMode);

  const isMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const [headerMixinsRef, setHeaderMixinsRef] = useState<null | HTMLDivElement>(null);

  React.useEffect(() => {
    setShellMode(getInitialShellMode());
  }, []);

  const isHeadless = shellMode === 'headless';

  if (isErrored(userProfile))
    return (
      <Stack alignItems="center" justifyContent="center" height="100vh" direction="column">
        <Typography variant="h5">Error loading data for user</Typography>
        <Typography maxWidth={500} align="center">
          Do you have any assigned environments? If not, please contact your administrator to assign you to an
          environment.
          <br />
          Otherwise, if the issue persists, please contact support.
        </Typography>
      </Stack>
    );

  return (
    <>
      {!isHeadless && (
        <Styled.AppBar color="transparent" elevation={0} data-testid="header">
          <Styled.ReversedToolbar>
            <UserProfileMenu />
            <Items.EnvironmentSelector />
            {/* <Items.NotificationsToolbarItem /> */}
            <Stack flex="1 1 auto" direction="row" alignItems="center" ref={(ref) => setHeaderMixinsRef(ref)} />
          </Styled.ReversedToolbar>
        </Styled.AppBar>
      )}
      {!isHeadless && (
        <NavDrawer
          open={navOpen}
          onClose={() => setNavOpen(false)}
          sx={{ width: drawer.widths.normal }}
          variant={isMdUp ? 'permanent' : isSmUp ? 'mini' : 'temporary'}
        />
      )}
      <Styled.ContentWrapper
        style={
          isHeadless
            ? {
                marginLeft: 0,
                width: '100%',
                paddingLeft: 0,
              }
            : undefined
        }
      >
        {!isHeadless && <Toolbar sx={{ marginBottom: 2 }} />}
        <HeaderMixinContext.Provider
          value={{
            ref: headerMixinsRef,
          }}
        >
          {children}
        </HeaderMixinContext.Provider>
      </Styled.ContentWrapper>
    </>
  );
}

export function HeaderMixin({ children }: React.PropsWithChildren) {
  const { ref } = useContext(HeaderMixinContext);
  return <Portal container={ref}>{children}</Portal>;
}
